<script setup>
/**
 * THIS CHAT: who has it, what the assistant said and why, and the one tap that changes it.
 * THE PLAN §4 P11's teach loop, and ease package B1 (spec §1 P-1.6 to P-1.8).
 *
 * Everything real is here rather than in `ConversationWasloPanel.vue` because that file
 * is the `onErrorCaptured` boundary, and `onErrorCaptured` does not catch the component
 * that declares it. The split is the mechanism, not a tidy-up.
 *
 * THE ORDER IS THE QUESTION ORDER OF WHOEVER IS LOOKING:
 *
 *   1. WHO HAS THIS CHAT? One row, from Chatwoot's own store (status, assignee, labels),
 *      kept live by ActionCable. Pending and nothing blocking: the assistant, with "I'll
 *      take this one". Open or snoozed: a person, with "Give it back". Pending with a
 *      blocker: nobody, with the channel's A6 state and its one fix. Resolved: closed.
 *   2. WHAT DID IT SAY, AND WHY? The last turn, both halves, and one plain sentence with at
 *      most one fix (P-1.8).
 *   3. HOW IT ANSWERED, folded: what it looked for, and how close each saved answer was, in
 *      words. Never a percent.
 *   4. FIX IT: "Teach the right answer" for an administrator; "Suggest this answer to the
 *      owner" for a team member, who sees this pane and nothing else of Waslo AI (P-1.7).
 *   5. FOR ALL CHATS: the account switch, at the foot, administrators only.
 *
 * ASSIGNING A CHAT DOES NOT STOP THE ASSISTANT, and this pane no longer says it does. The
 * chat's STATUS is the one truth: pending is the assistant's, open is a person's. The sidecar's
 * `SKIP_REPLY_WHEN_HUMAN_ASSIGNED` is false and must stay so, because a handover always leaves
 * an assignee and a resolved chat reopens pending with it still set.
 *
 * THE PANE IS 320px WIDE (`ConversationSidebar.vue`: `md:w-[320px]`, 360 at 2xl): one
 * column, `text-xs` for everything secondary, and the customer's and assistant's own text
 * clamped, never truncated to one line.
 *
 * IT REFETCHES WHEN THE CONVERSATION CHANGES. `ContactPanel` renders these sections inside a
 * `Draggable` keyed by section NAME, so the next conversation changes the prop without a
 * remount; without the watch, an agent working down her queue would read the last one's reply.
 */
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import { useWasloI18n } from 'dashboard/routes/dashboard/wasloai/i18n.js';
// The section's own icon, from the one file that defines it.
import { WASLO_SIDEBAR_ICON } from 'dashboard/routes/dashboard/wasloai/nav.js';
import { useWasloApi } from 'dashboard/routes/dashboard/wasloai/composables/useWasloApi.js';
import { useWasloAccountId } from 'dashboard/routes/dashboard/wasloai/composables/useWasloAccountId.js';
import { useChatOwnership } from 'dashboard/routes/dashboard/wasloai/composables/useChatOwnership.js';
import { wasloApi } from 'dashboard/routes/dashboard/wasloai/api/client.js';
import { relativeTime } from 'dashboard/routes/dashboard/wasloai/helpers/relative-time.js';
import { receiptPhrase } from 'dashboard/routes/dashboard/wasloai/helpers/ai-price.js';
import {
  CHAT_ROWS,
  canGiveBack,
  chatRow,
} from 'dashboard/routes/dashboard/wasloai/helpers/chat-ownership.js';
import {
  MARKER,
  isolateParts,
} from 'dashboard/routes/dashboard/wasloai/helpers/bidi.js';
import TeachAnswerDialog from 'dashboard/routes/dashboard/wasloai/components/TeachAnswerDialog.vue';
import AssistantStatus from 'dashboard/routes/dashboard/wasloai/components/AssistantStatus.vue';
import AiSwitch from 'dashboard/routes/dashboard/wasloai/components/AiSwitch.vue';
import WhyLine from 'dashboard/routes/dashboard/wasloai/components/WhyLine.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t, locale } = useWasloI18n();
const accountId = useWasloAccountId();

/** The prop is the only authority, exactly as the route is for the account. */
const conversationId = computed(() => {
  const parsed = Number(props.conversationId);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
});

// One read for administrators and staff alike (the member route, P-1.7). `lastTurn: null` is a
// REAL answer ("it has not replied here"), never EMPTY.
const panel = useWasloApi(
  signal =>
    wasloApi.getChatPanel(accountId.value, conversationId.value, signal),
  { isEmpty: () => false }
);

const data = computed(() => panel.data.value);
const lastTurn = computed(() => data.value?.lastTurn ?? null);
const isAdmin = computed(() => data.value?.isAdmin === true);

// ── 1 · who has this chat ──────────────────────────────────────────────────────────
const ownership = useChatOwnership(conversationId);
const channel = computed(() => data.value?.status ?? null);
const row = computed(() =>
  chatRow({
    chatStatus: ownership.status.value,
    channelState: channel.value?.state ?? null,
  })
);
/** A blocker on this chat's channel: the reason nobody answers a pending chat here. */
const blocked = computed(
  () => channel.value !== null && !canGiveBack(channel.value.state)
);
const practising = computed(() => channel.value?.state === 'practice');

/** "You're handling this", "Dana is handling this" (a bidi isolate), or "Your team is". */
const personParts = computed(() => {
  const person = ownership.assignee.value;
  if (person === null) {
    return { before: t('WASLO.PANEL.CHAT.TEAM'), value: '', after: '' };
  }
  if (person.id === ownership.me.value) {
    return { before: t('WASLO.PANEL.CHAT.YOU'), value: '', after: '' };
  }
  return isolateParts(
    t('WASLO.PANEL.CHAT.SOMEONE', { name: MARKER }),
    person.name
  );
});

const rowTitle = computed(() => {
  if (row.value === CHAT_ROWS.ANSWERING) {
    return { before: t('WASLO.PANEL.CHAT.ANSWERING'), value: '', after: '' };
  }
  if (row.value === CHAT_ROWS.PERSON) return personParts.value;
  if (row.value === CHAT_ROWS.BLOCKED) {
    return { before: t('WASLO.PANEL.CHAT.BLOCKED'), value: '', after: '' };
  }
  if (row.value === CHAT_ROWS.CLOSED) {
    return { before: t('WASLO.PANEL.CHAT.CLOSED'), value: '', after: '' };
  }
  return null;
});

const isChanging = ref(false);
const changeError = ref('');

const change = async write => {
  if (isChanging.value) return;
  isChanging.value = true;
  changeError.value = '';
  try {
    const landed = await write();
    if (!landed) changeError.value = t('WASLO.PANEL.CHAT.FAILED');
  } catch {
    changeError.value = t('WASLO.PANEL.CHAT.FAILED');
  } finally {
    isChanging.value = false;
  }
};

const takeOver = () => change(ownership.takeOver);
const giveBack = () => change(ownership.giveBack);

// ── the saved answer or suggestion, and the conversation it belongs to ─────────────
const saved = ref(null);
const suggested = ref(false);

watch(conversationId, () => {
  saved.value = null;
  suggested.value = false;
  changeError.value = '';
  if (conversationId.value !== null) panel.reload();
});

const errorMessage = computed(() => {
  if (conversationId.value === null) return t('WASLO.PANEL.ERROR');
  return panel.error.value ? t('WASLO.PANEL.ERROR') : '';
});

const isFetching = computed(
  () => !errorMessage.value && panel.isFetching.value
);

const when = computed(() => {
  const at = lastTurn.value?.at;
  if (!at) return '';
  return (
    relativeTime(at, locale.value, {
      justNow: t('WASLO.PANEL.STATUS.JUST_NOW'),
    }) ?? ''
  );
});

// ── how it answered ────────────────────────────────────────────────────────────────
const isTraceOpen = ref(false);
const lookups = computed(() => lastTurn.value?.lookups ?? []);
const actions = computed(() => lastTurn.value?.actions ?? []);
const hasTrace = computed(
  () => lookups.value.length > 0 || actions.value.length > 0
);

const CLOSENESS_COPY = { close: 'CLOSE', loose: 'LOOSE', none: 'NONE' };
const closenessLabel = lookup =>
  t(`WASLO.PANEL.TRACE.${CLOSENESS_COPY[lookup.closeness] ?? 'NONE'}`);

/**
 * Three nodes, never one string: the middle one is a bidi ISOLATE, because what it searched
 * for is routinely Arabic inside an English frame.
 */
const queryParts = query =>
  isolateParts(t('WASLO.PANEL.TRACE.LOOKUP_QUERY', { query: MARKER }), query);

// ── teaching (administrator) and suggesting (team member) ──────────────────────────
const teachRef = ref(null);
const suggestRef = ref(null);

const openFix = () => {
  saved.value = null;
  suggested.value = false;
  if (isAdmin.value) teachRef.value?.open();
  else suggestRef.value?.open();
};

const onTaught = answer => {
  saved.value = answer;
};

const onSuggested = () => {
  suggested.value = true;
};

const savedTitle = computed(() =>
  saved.value?.created
    ? t('WASLO.PANEL.TEACH.SAVED')
    : t('WASLO.PANEL.TEACH.REPLACED')
);
const savedTotal = computed(() =>
  saved.value ? t('WASLO.PANEL.TEACH.TOTAL', saved.value.total) : ''
);
// P-5.12: what the teach cost, from the server's `charged`.
const savedReceipt = computed(() =>
  saved.value ? receiptPhrase(t, locale.value, saved.value.charged) : ''
);

// ── 5 · for all chats ──────────────────────────────────────────────────────────────
const accountOn = ref(false);
watch(
  () => data.value?.accountOn,
  value => {
    accountOn.value = value === true;
  },
  { immediate: true }
);
// The switch saves the moment it is flipped (A6's `AiSwitch`); the channel's state is read
// again, because turning the assistant off or on changes it.
const onSwitchSaved = () => panel.reload();

// ── the way out (administrators) ───────────────────────────────────────────────────
//
// RESOLVED BEFORE IT IS RENDERED: `router-link` throws during its own setup on a route name
// the router does not carry, which would take this section down in every conversation.
const router = useRouter();

// Who may leave for the section. The panel's own answer when there is one; when the read
// failed (exactly when she wants to go and look properly), Chatwoot's own role. No store means
// a harness, read as an administrator, as `useWasloProvisioned` does.
const store = getCurrentInstance()?.proxy?.$store ?? null;
const mayOpen = computed(() => {
  if (data.value) return isAdmin.value;
  if (store === null) return true;
  return store.getters?.getCurrentRole === 'administrator';
});

const overviewLink = computed(() => {
  if (accountId.value === null || !mayOpen.value) return null;
  const to = { name: 'waslo_overview', params: { accountId: accountId.value } };
  try {
    router.resolve(to);
    return to;
  } catch {
    return null;
  }
});
</script>

<template>
  <div class="flex flex-col gap-4 px-3 py-4" data-waslo-panel-body>
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-6 text-n-slate-11"
      data-waslo-panel-state="loading"
    >
      <Spinner />
    </div>

    <p
      v-else-if="errorMessage"
      class="mb-0 text-xs leading-5 text-n-slate-11"
      data-waslo-panel-state="error"
    >
      {{ errorMessage }}
    </p>

    <template v-else-if="data">
      <!-- 1 · WHO HAS THIS CHAT? From Chatwoot's own store; absent in a harness with none. -->
      <section
        v-if="rowTitle"
        class="flex flex-col gap-2"
        :data-waslo-chat-row="row"
      >
        <p
          class="mb-0 text-sm font-medium leading-5 text-n-slate-12"
          dir="auto"
          data-waslo-chat-title
        >
          <span>{{ rowTitle.before }}</span>
          <bdi v-if="rowTitle.value">{{ rowTitle.value }}</bdi>
          <span>{{ rowTitle.after }}</span>
        </p>
        <p
          v-if="row === 'answering' && practising"
          class="mb-0 text-xs leading-5 text-n-slate-11"
          data-waslo-chat-practice
        >
          {{ t('WASLO.PANEL.CHAT.PRACTICE_NOTE') }}
        </p>
        <Button
          v-if="row === 'answering'"
          :label="
            isChanging
              ? t('WASLO.PANEL.CHAT.WORKING')
              : t('WASLO.PANEL.CHAT.TAKE')
          "
          size="sm"
          variant="faded"
          color="slate"
          :is-loading="isChanging"
          class="self-start"
          data-waslo-chat-take
          @click="takeOver"
        />
        <Button
          v-else-if="row === 'person' && !blocked"
          :label="
            isChanging
              ? t('WASLO.PANEL.CHAT.WORKING')
              : t('WASLO.PANEL.CHAT.GIVE_BACK')
          "
          size="sm"
          variant="faded"
          color="slate"
          :is-loading="isChanging"
          class="self-start"
          data-waslo-chat-give-back
          @click="giveBack"
        />
        <p
          v-if="changeError"
          class="mb-0 text-xs leading-5 text-n-ruby-11"
          data-waslo-chat-failed
        >
          {{ changeError }}
        </p>
      </section>

      <!-- The channel's own state and its one fix: why nobody answers here, or why the chat
           cannot go back yet. Also shown with no store, so a harness still says it. -->
      <AssistantStatus
        v-if="blocked && row !== 'closed'"
        size="small"
        :status="channel"
        :account-id="accountId"
        :inbox-id="channel.inboxId"
        :asks-owner="!isAdmin"
        data-waslo-panel-status
        data-waslo-panel-channel-status
        @changed="panel.reload()"
      />

      <p
        v-if="data.turns === 0"
        class="mb-0 text-xs leading-5 text-n-slate-11"
        data-waslo-panel-quiet
      >
        {{ t('WASLO.PANEL.STATUS.QUIET') }}
      </p>

      <!-- 2 · WHAT DID IT SAY, AND WHY? -->
      <section
        v-if="lastTurn"
        class="flex flex-col gap-3 p-3 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
        data-waslo-panel-last
      >
        <div class="flex items-baseline justify-between gap-2">
          <h4 class="mb-0 text-xs font-medium text-n-slate-12">
            {{ t('WASLO.PANEL.LAST.HEADING') }}
          </h4>
          <span v-if="when" class="text-xs shrink-0 text-n-slate-10">
            {{ when }}
          </span>
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-10">
            {{ t('WASLO.PANEL.LAST.CUSTOMER') }}
          </span>
          <bdi
            class="text-xs leading-5 break-words text-n-slate-12 line-clamp-3"
            data-waslo-panel-customer
          >
            {{ lastTurn.customer }}
          </bdi>
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-10">
            {{ t('WASLO.PANEL.LAST.ASSISTANT') }}
          </span>
          <p
            v-if="!lastTurn.assistant"
            class="mb-0 text-xs leading-5 text-n-slate-11"
            data-waslo-panel-silent
          >
            {{ t('WASLO.PANEL.LAST.SILENT') }}
          </p>
          <bdi
            v-else
            class="text-xs leading-5 break-words text-n-slate-12 line-clamp-6"
            data-waslo-panel-reply
          >
            {{ lastTurn.assistant }}
          </bdi>
        </div>

        <WhyLine
          :why="lastTurn.why"
          :account-id="accountId"
          :is-admin="isAdmin"
          @teach="openFix"
          @suggest="openFix"
          @changed="panel.reload()"
        />

        <!-- 3 · HOW IT ANSWERED -->
        <div class="flex flex-col" data-waslo-panel-trace>
          <button
            type="button"
            class="flex items-center min-w-0 gap-1.5 py-1 text-xs text-n-slate-11 hover:text-n-slate-12"
            :aria-expanded="isTraceOpen"
            data-waslo-panel-trace-toggle
            @click="isTraceOpen = !isTraceOpen"
          >
            <span
              class="transition-transform duration-200 shrink-0 size-3.5 i-lucide-chevron-down"
              :class="isTraceOpen ? 'rotate-180' : ''"
              aria-hidden="true"
            />
            <span class="font-medium">
              {{
                isTraceOpen
                  ? t('WASLO.PANEL.TRACE.HIDE')
                  : t('WASLO.PANEL.TRACE.SHOW')
              }}
            </span>
          </button>

          <div v-if="isTraceOpen" class="flex flex-col gap-3 pt-2">
            <p
              v-if="!hasTrace"
              class="mb-0 text-xs leading-5 text-n-slate-11"
              data-waslo-panel-trace-nothing
            >
              {{ t('WASLO.PANEL.TRACE.NOTHING') }}
            </p>

            <div
              v-for="(lookup, index) in lookups"
              :key="`${lookup.query}-${index}`"
              class="flex flex-col gap-1"
              data-waslo-panel-lookup
            >
              <p class="mb-0 text-xs leading-5 text-n-slate-11" dir="auto">
                <span>{{ queryParts(lookup.query).before }}</span>
                <bdi class="text-n-slate-12">{{
                  queryParts(lookup.query).value
                }}</bdi>
                <span>{{ queryParts(lookup.query).after }}</span>
              </p>
              <p
                v-if="lookup.matches === 0"
                class="mb-0 text-xs leading-5 text-n-slate-10"
              >
                {{ t('WASLO.PANEL.TRACE.LOOKUP_NONE') }}
              </p>
              <p v-else class="mb-0 text-xs leading-5 text-n-slate-10">
                {{ closenessLabel(lookup) }}
              </p>
            </div>

            <div v-if="actions.length" class="flex flex-col gap-1">
              <span class="text-xs text-n-slate-10">
                {{ t('WASLO.PANEL.TRACE.ACTIONS_HEADING') }}
              </span>
              <bdi
                v-for="(name, index) in actions"
                :key="`${name}-${index}`"
                class="text-xs break-words text-n-slate-12"
              >
                {{ name }}
              </bdi>
            </div>
          </div>
        </div>
      </section>

      <!-- 4 · FIX IT: teach (administrator) or suggest (team member). -->
      <div class="flex flex-col gap-2">
        <Button
          v-if="isAdmin"
          :label="
            saved ? t('WASLO.PANEL.TEACH.AGAIN') : t('WASLO.PANEL.TEACH.OPEN')
          "
          icon="i-lucide-graduation-cap"
          size="sm"
          class="w-full"
          data-waslo-panel-teach
          @click="openFix"
        />
        <Button
          v-else
          :label="t('WASLO.PANEL.SUGGEST.OPEN')"
          icon="i-lucide-lightbulb"
          size="sm"
          class="w-full"
          data-waslo-panel-suggest
          @click="openFix"
        />
        <div v-if="saved" class="flex flex-col gap-0.5" data-waslo-panel-saved>
          <p class="mb-0 text-xs leading-5 text-n-teal-11">{{ savedTitle }}</p>
          <p class="mb-0 text-xs leading-5 text-n-slate-10">
            {{ savedTotal }}
          </p>
          <p
            v-if="savedReceipt"
            class="mb-0 text-xs leading-5 text-n-slate-10"
            data-waslo-panel-receipt
          >
            {{ savedReceipt }}
          </p>
        </div>
        <p
          v-if="suggested"
          class="mb-0 text-xs leading-5 text-n-teal-11"
          data-waslo-panel-suggested
        >
          {{ t('WASLO.PANEL.SUGGEST.SENT') }}
        </p>
      </div>

      <!-- 5 · FOR ALL CHATS. Administrators only; Overview stays its main home. -->
      <section
        v-if="isAdmin && data.accountOn !== null"
        class="flex items-center justify-between gap-2 pt-3 border-t border-n-weak"
        data-waslo-panel-footer
      >
        <span class="text-xs font-medium leading-5 text-n-slate-11">
          {{ t('WASLO.PANEL.FOOTER.ALL_CHATS') }}
        </span>
        <AiSwitch
          v-model="accountOn"
          :account-id="accountId"
          @saved="onSwitchSaved"
        />
      </section>
    </template>

    <!-- THE WAY OUT, administrators only: staff see this pane and nothing else of Waslo AI. -->
    <router-link
      v-if="overviewLink"
      :to="overviewLink"
      class="flex items-center gap-1.5 py-1 text-xs font-medium transition-colors text-n-slate-11 hover:text-n-slate-12"
      data-waslo-panel-open
    >
      <span
        class="shrink-0 size-3.5"
        :class="WASLO_SIDEBAR_ICON"
        aria-hidden="true"
      />
      <span>{{ t('WASLO.PANEL.OPEN') }}</span>
    </router-link>

    <TeachAnswerDialog
      v-if="isAdmin"
      ref="teachRef"
      :account-id="accountId"
      :conversation-id="conversationId"
      :suggested-question="data ? data.suggestedQuestion : ''"
      @taught="onTaught"
    />
    <TeachAnswerDialog
      v-else-if="data"
      ref="suggestRef"
      mode="suggest"
      :account-id="accountId"
      :conversation-id="conversationId"
      :suggested-question="data.suggestedQuestion"
      @suggested="onSuggested"
    />
  </div>
</template>
